/**
 * TeeInBlue Cart Image Loader
 * Handles lazy loading of customization images in the cart drawer
 * Images may not be ready immediately after adding to cart
 */

class CartTeeImageLoader {
  constructor() {
    this.maxRetries = 20; // Maximum number of retry attempts
    this.retryInterval = 500; // Retry every 500ms
    this.imageCheckers = new Map(); // Track active checkers per image

    this.init();
  }

  init() {
    // Check images when cart drawer opens
    document.addEventListener('cart:updated', () => this.checkAllImages());

    // Check images when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.checkAllImages());
    } else {
      this.checkAllImages();
    }

    // Use MutationObserver to detect when new cart items are added
    this.observeCartChanges();
  }

  observeCartChanges() {
    const cartContainers = document.querySelectorAll('[data-cart-drawer], [data-cart-items], .cart-drawer, .cart-items');

    cartContainers.forEach(container => {
      if (!container) return;

      const observer = new MutationObserver(() => {
        this.checkAllImages();
      });

      observer.observe(container, {
        childList: true,
        subtree: true
      });
    });
  }

  checkAllImages() {
    const teeImages = document.querySelectorAll('.tee-cart-thumbnail__img');

    teeImages.forEach(img => {
      // Skip if already loaded successfully
      if (img.complete && img.naturalHeight !== 0) {
        return;
      }

      // Skip if already being checked
      if (this.imageCheckers.has(img)) {
        return;
      }

      // Start checking this image
      this.startImageCheck(img);
    });
  }

  startImageCheck(img) {
    let attempts = 0;
    const originalSrc = img.src;

    const checker = setInterval(() => {
      attempts++;

      // Check if image is now loaded
      if (img.complete && img.naturalHeight !== 0) {
        this.onImageLoaded(img, checker);
        return;
      }

      // Retry loading by refreshing the src
      if (attempts % 2 === 0) {
        // Add cache-busting parameter every other attempt
        const separator = originalSrc.includes('?') ? '&' : '?';
        img.src = `${originalSrc}${separator}_retry=${Date.now()}`;
      }

      // Stop after max retries
      if (attempts >= this.maxRetries) {
        this.onImageFailed(img, checker);
      }
    }, this.retryInterval);

    this.imageCheckers.set(img, checker);
  }

  onImageLoaded(img, checker) {
    clearInterval(checker);
    this.imageCheckers.delete(img);

    // Add a class to indicate successful load
    img.classList.add('tee-image-loaded');

    // Optional: Add fade-in animation
    img.style.opacity = '0';
    setTimeout(() => {
      img.style.transition = 'opacity 0.3s ease';
      img.style.opacity = '1';
    }, 10);
  }

  onImageFailed(img, checker) {
    clearInterval(checker);
    this.imageCheckers.delete(img);

    console.warn('TeeInBlue image failed to load after max retries:', img.src);

    // Add a class to indicate failed load
    img.classList.add('tee-image-failed');
  }
}

// Initialize when script loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartTeeImageLoader();
  });
} else {
  new CartTeeImageLoader();
}
